import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, User, Car, ClipboardList, Wrench, Package, ReceiptText,
  Camera, CreditCard, ClipboardCheck, CalendarClock, Truck, Activity,
  Plus, X, ChevronRight, AlertTriangle, Check, MoreHorizontal, Send,
  Printer, ChevronDown, FileText, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_META, PRIORITIES, PRIMARY_ACTION, remapStatus, inr, SERVICE_TYPES, REPAIR_TYPES } from "./lib";
import PhotoUploader from "./components/PhotoUploader";
import DamageMarker from "./components/DamageMarker";
import EnquiryDialog from "@/pages/spare-parts/components/EnquiryDialog";
import IssuePartDialog from "./components/IssuePartDialog";
import { downloadJobCardPdf } from "@/lib/pdf";
import WorkflowStrip from "@/components/WorkflowStrip";

// After the Feb 2026 8-state simplification, a JC is billable once it hits QC.
const BILLABLE_STATUSES = new Set([
  "quality_check", "ready_for_delivery", "closed",
]);

const TABS = [
  { key: "overview",     label: "Overview",     Icon: ClipboardList },
  { key: "tasks",        label: "Tasks",        Icon: Wrench        },
  { key: "inspection",   label: "Inspection",   Icon: Camera        },
  { key: "parts",        label: "Parts",        Icon: Package       },
  { key: "estimate",     label: "Estimate",     Icon: ReceiptText   },
  { key: "photos",       label: "Photos",       Icon: Camera        },
  { key: "payments",     label: "Payments",     Icon: CreditCard    },
  { key: "quality",      label: "Quality Check", Icon: ClipboardCheck },
  { key: "future",       label: "Future Work",  Icon: CalendarClock },
  { key: "delivery",     label: "Delivery",     Icon: Truck         },
  { key: "activity",     label: "Activity",     Icon: Activity      },
];

/**
 * Tabbed job-card detail workspace (Section 26).
 */

// Legacy documents may store scalars (e.g. `complaints: "Updated complaint"`)
// where the current schema expects an array. Coerce every array-shaped field
// once at the fetch boundary so downstream `.map()` sites can never crash.
const ARRAY_FIELDS = [
  "complaints", "tasks", "parts_needed", "estimates", "damage_markers",
  "activity", "future_work", "photos", "checklist", "status_history",
];
function sanitizeJc(jc) {
  if (!jc || typeof jc !== "object") return jc;
  const out = { ...jc };
  for (const k of ARRAY_FIELDS) {
    const v = out[k];
    if (Array.isArray(v)) continue;
    if (v == null || v === "") { out[k] = []; continue; }
    // Preserve a scalar so it can still be shown (e.g. legacy string complaint)
    if (k === "complaints" && typeof v === "string") {
      out[k] = [{ id: "legacy-complaint", text: v }];
    } else {
      out[k] = [];
    }
  }
  // Ensure nested object-shaped fields default to `{}` so `x.foo` accessors don't crash.
  if (out.customer_snapshot == null || typeof out.customer_snapshot !== "object" || Array.isArray(out.customer_snapshot)) out.customer_snapshot = {};
  if (out.vehicle_snapshot == null || typeof out.vehicle_snapshot !== "object" || Array.isArray(out.vehicle_snapshot)) out.vehicle_snapshot = {};
  if (out.findings == null || typeof out.findings !== "object" || Array.isArray(out.findings)) out.findings = {};
  if (out.advance == null || typeof out.advance !== "object" || Array.isArray(out.advance)) out.advance = {};
  return out;
}

export default function JobCardDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { garage } = useAuth();
  const [jc, setJc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [advisors, setAdvisors] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [statusOpen, setStatusOpen] = useState(false);
  const [linkedInvoice, setLinkedInvoice] = useState(null);
  const [linkedGatePass, setLinkedGatePass] = useState(null);
  const [linkedEstimate, setLinkedEstimate] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/job-cards/${id}`);
      setJc(sanitizeJc(r.data));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load job card");
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/job-cards/service-advisors").then((r) => setAdvisors(r.data || [])).catch(() => {});
    api.get("/job-cards/mechanics").then((r) => setMechanics(r.data || [])).catch(() => {});
  }, []);

  // Load workflow linkage (invoice, gate pass, estimate) once JC id is known.
  useEffect(() => {
    if (!jc?.id) return;
    api.get("/billing/invoices", { params: { job_card_id: jc.id } })
      .then((r) => {
        const rows = (r.data || []).filter((i) => i.status !== "cancelled");
        setLinkedInvoice(rows[0] || null);
      })
      .catch(() => setLinkedInvoice(null));
    api.get("/billing/gate-passes", { params: { job_card_id: jc.id } })
      .then((r) => {
        const rows = (r.data || [])
          .filter((g) => g.status !== "cancelled")
          .filter((g) => g.job_card_id === jc.id);   // defence-in-depth
        setLinkedGatePass(rows[0] || null);
      })
      .catch(() => setLinkedGatePass(null));
    if (jc.estimate_id) {
      api.get(`/estimates/${jc.estimate_id}`)
        .then((r) => setLinkedEstimate(r.data || null))
        .catch(() => setLinkedEstimate(null));
    } else {
      setLinkedEstimate(null);
    }
  }, [jc?.id, jc?.estimate_id]);

  if (loading) {
    return <div className="p-10 text-center text-[13px] text-[#667085]" data-testid="jc-detail-loading">Loading…</div>;
  }
  if (!jc) {
    return <div className="p-10 text-center text-[13px] text-[#667085]">Job card not found</div>;
  }

  const status = remapStatus(jc.status);
  const primary = PRIMARY_ACTION[status];

  const setStatus = async (next, note = "") => {
    try {
      await api.post(`/job-cards/${jc.id}/status`, { status: next, note });
      toast.success(`Moved to ${STATUS_META[next]?.label || next}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update status"); }
  };

  return (
    <div className="pb-24 md:pb-8" data-testid="jc-detail-page">
      {/* Header */}
      <header className="flex items-start gap-3 mb-4">
        <Link
          to="/job-cards"
          className="w-9 h-9 grid place-items-center rounded-md hover:bg-[#F1FAEE] text-[#457b9d] mt-1"
          data-testid="jc-detail-back"
        >
          <ArrowLeft size={17} />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-[20px] md:text-[24px] font-bold text-[#1d3557] font-mono">
              {jc.number || jc.job_number}
            </h1>
            <StatusChipBig status={status} />
            <StatusPicker
              current={status}
              open={statusOpen}
              onOpen={setStatusOpen}
              onChoose={setStatus}
            />
          </div>
          <p className="text-[12px] text-[#667085] mt-0.5 flex flex-wrap items-center gap-x-4 gap-y-0.5">
            <span className="inline-flex items-center gap-1"><User size={11} />{jc.customer?.name || jc.customer_snapshot?.name || "—"}</span>
            <span className="inline-flex items-center gap-1"><Car size={11} className="text-[#457b9d]" />
              <span className="font-mono">{jc.vehicle?.reg_number || jc.vehicle_snapshot?.reg_number || "—"}</span>
              <span>{[jc.vehicle?.make || jc.vehicle_snapshot?.make, jc.vehicle?.model || jc.vehicle_snapshot?.model].filter(Boolean).join(" ")}</span>
            </span>
            {jc.service_type && <span>· {jc.service_type}</span>}
            {jc.promised_at && <span>· due {new Date(jc.promised_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
          </p>
        </div>
        {primary && (
          <button
            type="button"
            onClick={() => setStatus(primary.next)}
            data-testid="jc-primary-action"
            className="h-10 px-4 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5"
          >
            {primary.label} <ChevronRight size={13} />
          </button>
        )}
        <div className="relative inline-flex" data-testid="jc-pdf-group">
          <button
            type="button"
            onClick={() => downloadJobCardPdf(jc, { copyType: "customer", garage })}
            data-testid="jc-pdf-btn"
            className="h-10 pl-3 pr-2.5 rounded-l-md border border-[#E4E7EC] text-[12px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE] inline-flex items-center gap-1.5"
          >
            <Printer size={13} /> PDF
          </button>
          <button
            type="button"
            onClick={() => downloadJobCardPdf(jc, { copyType: "internal", garage })}
            data-testid="jc-pdf-internal-btn"
            title="Internal copy (workshop) — includes findings, quality check, all notes"
            className="h-10 px-2 rounded-r-md border border-l-0 border-[#E4E7EC] text-[10.5px] font-semibold text-[#e63946] hover:bg-[#e63946]/8"
          >
            INT
          </button>
        </div>
      </header>

      {/* Guided workflow: Estimate → Job Card → Invoice → Gate Pass */}
      <WorkflowStrip
        currentStep="job_card"
        estimate={linkedEstimate ? { id: linkedEstimate.id, number: linkedEstimate.number } : null}
        jobCard={{ id: jc.id, number: jc.number || jc.job_number, status }}
        invoice={linkedInvoice ? { id: linkedInvoice.id, number: linkedInvoice.number, status: linkedInvoice.status } : null}
        gatePass={linkedGatePass ? { id: linkedGatePass.id, number: linkedGatePass.number, status: linkedGatePass.status } : null}
        nextAction={
          linkedGatePass
            ? null
            : linkedInvoice
              ? {
                  label: "Create Gate Pass",
                  to: `/gate-pass/new?invoice=${linkedInvoice.id}`,
                  testId: "jc-workflow-gate-pass-btn",
                  hint: `Invoice ${linkedInvoice.number} is issued. Now generate a gate pass to release the vehicle.`,
                }
              : BILLABLE_STATUSES.has(status)
                ? {
                    label: "Convert to Invoice",
                    to: `/invoices/new?job_card=${jc.id}`,
                    testId: "jc-workflow-invoice-btn",
                    hint: "The job is completed. Convert this job card into a customer invoice.",
                  }
                : null
        }
        blockReason={
          !linkedInvoice && !BILLABLE_STATUSES.has(status)
            ? `Finish the work and move the job card to "Ready for delivery" — then you can generate the invoice.`
            : linkedGatePass
              ? `Gate pass ${linkedGatePass.number} is ready. The workflow is complete.`
              : null
        }
      />

      {/* Tabs */}
      <nav className="bg-white border border-[#E4E7EC] rounded-xl p-1 mb-4 overflow-x-auto" data-testid="jc-tabs">
        <div className="flex gap-1">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`jc-tab-${key}`}
              className={`h-9 px-3 rounded-md text-[12px] font-semibold whitespace-nowrap inline-flex items-center gap-1.5 transition-colors ${
                tab === key ? "bg-[#1d3557] text-white" : "text-[#667085] hover:text-[#1d3557]"
              }`}
            >
              <Icon size={12} /> {label}
            </button>
          ))}
        </div>
      </nav>

      {tab === "overview"    && <OverviewTab jc={jc} advisors={advisors} onReload={load} />}
      {tab === "tasks"       && <TasksTab jc={jc} mechanics={mechanics} onReload={load} />}
      {tab === "inspection"  && <InspectionTab jc={jc} onReload={load} />}
      {tab === "parts"       && <PartsTab jc={jc} onReload={load} />}
      {tab === "estimate"    && <EstimateTab jc={jc} />}
      {tab === "photos"      && <PhotosTab jc={jc} onReload={load} />}
      {tab === "payments"    && <PaymentsTab jc={jc} onReload={load} />}
      {tab === "quality"     && <QualityCheckTab jc={jc} onReload={load} />}
      {tab === "future"      && <FutureWorkTab jc={jc} onReload={load} />}
      {tab === "delivery"    && <DeliveryTab jc={jc} onReload={load} />}
      {tab === "activity"    && <ActivityTab jc={jc} onReload={load} />}
    </div>
  );
}

/* ---------- Tabs ---------- */

function OverviewTab({ jc, advisors, onReload }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="jc-overview">
      <div className="lg:col-span-2 space-y-4">
        <Card title="Customer complaints" testId="jc-complaints">
          {(jc.complaints || []).length === 0 ? (
            <Empty>No complaints recorded.</Empty>
          ) : (
            <ul className="space-y-2 text-[13px]">
              {jc.complaints.map((c) => (
                <li key={c.id} className="border-l-2 border-[#e63946]/50 pl-2.5">
                  <div className="text-[#1d3557]">{c.text}</div>
                  {c.category && <div className="text-[10.5px] text-[#667085] uppercase tracking-wider">{c.category}</div>}
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Service advisor findings" testId="jc-findings">
          {jc.findings?.initial_observation || jc.findings?.probable_cause ? (
            <div className="text-[13px] space-y-2">
              {jc.findings.initial_observation && <div><b>Initial:</b> {jc.findings.initial_observation}</div>}
              {jc.findings.probable_cause && <div><b>Cause:</b> {jc.findings.probable_cause}</div>}
              {jc.findings.recommended_inspection && <div><b>Recommended:</b> {jc.findings.recommended_inspection}</div>}
              <div className="text-[11.5px] text-[#667085] flex gap-3">
                {jc.findings.immediate_attention && <span>⚠︎ Immediate attention</span>}
                {jc.findings.road_test_required && <span>🚗 Road test required</span>}
              </div>
            </div>
          ) : (
            <Empty>Findings will appear once inspection begins.</Empty>
          )}
        </Card>

        <Card title="Snapshot" testId="jc-snapshot">
          <dl className="grid grid-cols-2 md:grid-cols-3 gap-3 text-[12.5px]">
            <FieldRow k="Service type" v={jc.service_type} />
            <FieldRow k="Repair type" v={REPAIR_TYPES.find((r) => r.key === jc.repair_type)?.label || "—"} />
            <FieldRow k="Priority" v={PRIORITIES.find((p) => p.key === jc.priority)?.label || jc.priority} />
            <FieldRow k="Odometer" v={jc.odometer ? `${jc.odometer} km` : "—"} />
            <FieldRow k="Fuel" v={jc.fuel_level ? jc.fuel_level.replace("_", " ") : "—"} />
            <FieldRow k="Belongings" v={jc.belongings} />
          </dl>
        </Card>
      </div>

      <div className="space-y-4">
        <Card title="Assignment" testId="jc-assignment">
          <dl className="space-y-2 text-[12.5px]">
            <FieldRow k="Service advisor" v={advisors.find((a) => a.id === jc.service_advisor_id)?.name || "Unassigned"} />
            <FieldRow k="Expected delivery" v={jc.promised_at ? new Date(jc.promised_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"} />
            <FieldRow k="Commitment" v={jc.customer_commitment_note} />
          </dl>
        </Card>

        <Card title="Advance payment" testId="jc-advance-panel">
          {jc.advance?.received ? (
            <div className="text-[13px]">
              <div className="text-[22px] font-bold text-[#12B76A] font-mono">{inr(jc.advance.amount)}</div>
              <div className="text-[11px] text-[#667085]">
                {jc.advance.mode?.replace("_", " ")}
                {jc.advance.reference_number ? ` · ${jc.advance.reference_number}` : ""}
              </div>
              {jc.advance.receipt_number && (
                <div className="text-[10.5px] font-mono text-[#457b9d] mt-1">Receipt: {jc.advance.receipt_number}</div>
              )}
            </div>
          ) : <Empty>No advance recorded.</Empty>}
        </Card>

        <Card title="Timing" testId="jc-timing">
          <dl className="space-y-2 text-[12.5px]">
            <FieldRow k="Created" v={jc.created_at ? new Date(jc.created_at).toLocaleString("en-IN") : "—"} />
            <FieldRow k="Updated" v={jc.updated_at ? new Date(jc.updated_at).toLocaleString("en-IN") : "—"} />
          </dl>
        </Card>
      </div>
    </div>
  );
}

function TasksTab({ jc, mechanics, onReload }) {
  const [name, setName] = useState("");
  const addTask = async () => {
    if (!name.trim()) return;
    try {
      await api.post(`/job-cards/${jc.id}/tasks`, { task_name: name.trim(), hours_est: 1 });
      setName(""); toast.success("Task added"); onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };
  const updateTask = async (tid, patch) => {
    try {
      await api.patch(`/job-cards/${jc.id}/tasks/${tid}`, patch);
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const removeTask = async (tid) => {
    if (!window.confirm("Remove this task?")) return;
    await api.delete(`/job-cards/${jc.id}/tasks/${tid}`);
    onReload();
  };

  return (
    <Card title="Tasks" testId="jc-tasks">
      <div className="flex gap-2 mb-3">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Add a task — e.g. Engine oil replacement"
          data-testid="jc-task-name-input"
          className="flex-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
          onKeyDown={(e) => e.key === "Enter" && addTask()}
        />
        <button
          type="button" onClick={addTask}
          data-testid="jc-task-add-btn"
          className="h-10 px-4 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold inline-flex items-center gap-1"
        >
          <Plus size={13} /> Add
        </button>
      </div>

      {(jc.tasks || []).length === 0 ? <Empty>No tasks yet.</Empty> : (
        <ul className="space-y-2" data-testid="jc-task-list">
          {jc.tasks.map((t) => (
            <li key={t.id} className="border border-[#E4E7EC] rounded-lg p-3">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#1d3557]">{t.task_name}</div>
                  <div className="text-[11px] text-[#667085] flex gap-2 flex-wrap mt-0.5">
                    {t.category && <span>#{t.category}</span>}
                    <span>{t.hours_est}h</span>
                  </div>
                </div>
                <select
                  value={t.status}
                  onChange={(e) => updateTask(t.id, { status: e.target.value })}
                  data-testid={`jc-task-status-${t.id}`}
                  className="h-8 px-2 rounded-md border border-[#E4E7EC] text-[11.5px] bg-white focus:border-[#457b9d] outline-none"
                >
                  {["not_started","in_progress","paused","waiting_for_parts","completed","quality_check","rework","cancelled"].map((s) => (
                    <option key={s} value={s}>{s.replace("_", " ")}</option>
                  ))}
                </select>
                <select
                  value={t.assigned_mechanic_id || ""}
                  onChange={(e) => updateTask(t.id, { assigned_mechanic_id: e.target.value || null })}
                  data-testid={`jc-task-mech-${t.id}`}
                  className="h-8 px-2 rounded-md border border-[#E4E7EC] text-[11.5px] bg-white focus:border-[#457b9d] outline-none"
                >
                  <option value="">Unassigned</option>
                  {mechanics.map((m) => (
                    <option key={m.id} value={m.id}>{m.name || m.email}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => removeTask(t.id)}
                  className="text-[#a8dadc] hover:text-[#e63946]"
                >
                  <X size={13} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function PartsTab({ jc, onReload }) {
  const [row, setRow] = useState({ name: "", qty: 1, unit_price: 0, part_number: "" });
  const [selected, setSelected] = useState({});
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [issueRow, setIssueRow] = useState(null);
  const addPart = async () => {
    if (!row.name.trim()) return;
    try {
      await api.post(`/job-cards/${jc.id}/parts`, row);
      setRow({ name: "", qty: 1, unit_price: 0, part_number: "" });
      toast.success("Part requirement added");
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };
  const updatePart = async (pid, patch) => {
    try { await api.patch(`/job-cards/${jc.id}/parts/${pid}`, patch); onReload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const removePart = async (pid) => {
    if (!window.confirm("Remove this part?")) return;
    await api.delete(`/job-cards/${jc.id}/parts/${pid}`);
    onReload();
  };
  const toggleSelected = (pid) => setSelected((s) => ({ ...s, [pid]: !s[pid] }));

  const parts = jc.parts_needed || [];
  const selectedList = parts.filter((p) => selected[p.id]);
  const enquiryItems = (selectedList.length ? selectedList : parts).map((p) => ({
    part_name: p.name || "",
    part_number: p.part_number || "",
    brand: p.brand || "",
    spec: p.spec || "",
    qty: p.qty || 1,
    notes: p.notes || "",
  }));
  const onEnquirySubmitted = async () => {
    // Move selected/all parts_needed rows to 'enquiry_submitted' status.
    const toUpdate = selectedList.length ? selectedList : parts;
    await Promise.all(
      toUpdate
        .filter((p) => !["issued", "received", "cancelled"].includes(p.status))
        .map((p) => api.patch(`/job-cards/${jc.id}/parts/${p.id}`, { status: "enquiry_submitted" }).catch(() => {}))
    );
    setSelected({});
    onReload();
  };

  return (
    <Card title="Parts requirements" testId="jc-parts"
      action={
        <button
          type="button"
          onClick={() => setEnquiryOpen(true)}
          disabled={parts.length === 0}
          className="h-9 px-3 rounded-md bg-[#1d3557] hover:bg-[#152941] text-white text-[11.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="jc-parts-submit-enquiry"
        >
          <Send size={11} /> Submit {selectedList.length > 0 ? `${selectedList.length} selected` : ""} to SparesHub
        </button>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-3">
        <input value={row.name} onChange={(e) => setRow({ ...row, name: e.target.value })}
          placeholder="Part name"
          data-testid="jc-part-name" className="md:col-span-2 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px]" />
        <input value={row.part_number} onChange={(e) => setRow({ ...row, part_number: e.target.value })}
          placeholder="Part number" className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px]" />
        <input type="number" value={row.qty} onChange={(e) => setRow({ ...row, qty: Number(e.target.value) })}
          placeholder="Qty" className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono" />
        <div className="flex gap-2">
          <input type="number" value={row.unit_price}
            onChange={(e) => setRow({ ...row, unit_price: Number(e.target.value) })}
            placeholder="Price" className="flex-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono" />
          <button
            type="button" onClick={addPart}
            data-testid="jc-part-add"
            className="h-10 px-4 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold"
          >
            Add
          </button>
        </div>
      </div>

      {parts.length === 0 ? <Empty>No parts required.</Empty> : (
        <ul className="space-y-2" data-testid="jc-part-list">
          {parts.map((p) => (
            <li key={p.id} className="border border-[#E4E7EC] rounded-lg p-3 flex items-center gap-3">
              <input type="checkbox" checked={!!selected[p.id]} onChange={() => toggleSelected(p.id)}
                     data-testid={`jc-part-select-${p.id}`}
                     className="w-4 h-4 accent-[#e63946]" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[#1d3557]">{p.name}</div>
                <div className="text-[11px] text-[#667085] flex flex-wrap gap-2">
                  <span>Qty: <b>{p.qty}</b></span>
                  {p.part_number && <span>#{p.part_number}</span>}
                  <span>{inr(p.unit_price)}</span>
                </div>
              </div>
              <select
                value={p.status}
                onChange={(e) => updatePart(p.id, { status: e.target.value })}
                data-testid={`jc-part-status-${p.id}`}
                className="h-8 px-2 rounded-md border border-[#E4E7EC] text-[11.5px]"
              >
                {["required","available_in_stock","reserved","issued","enquiry_pending","enquiry_submitted","ordered","partially_received","received","unavailable","cancelled"].map((s) => (
                  <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                ))}
              </select>
              {p.status !== "issued" && p.status !== "cancelled" && (
                <button type="button" onClick={() => setIssueRow(p)}
                        data-testid={`jc-part-issue-${p.id}`}
                        className="h-8 px-2.5 rounded-md bg-[#12B76A] hover:bg-[#0F9256] text-white text-[11px] font-semibold inline-flex items-center gap-1">
                  Issue
                </button>
              )}
              <button type="button" onClick={() => removePart(p.id)} className="text-[#a8dadc] hover:text-[#e63946]">
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <EnquiryDialog
        open={enquiryOpen}
        onClose={() => setEnquiryOpen(false)}
        onSubmitted={onEnquirySubmitted}
        initialItems={enquiryItems}
        jobCardId={jc.id}
        vehicleId={jc.vehicle_id}
        contextLabel={`For ${jc.number || jc.job_number} · ${jc.customer_snapshot?.name || ""}`}
      />
      <IssuePartDialog
        open={!!issueRow}
        onClose={() => setIssueRow(null)}
        jobCardId={jc.id}
        partsNeededRow={issueRow}
        onIssued={() => { setIssueRow(null); onReload(); }}
      />
    </Card>
  );
}

function EstimateTab({ jc }) {
  return (
    <Card title="Estimate" testId="jc-estimate-tab">
      {(jc.estimates || []).length > 0 ? (
        <ul className="space-y-2">
          {jc.estimates.map((e) => (
            <li key={e.id} className="flex items-center justify-between border border-[#E4E7EC] rounded-md p-3">
              <div>
                <div className="font-mono font-semibold text-[#1d3557]">{e.number}</div>
                <div className="text-[11px] text-[#667085]">{e.status}</div>
              </div>
              <div className="text-right">
                <div className="text-[13px] font-bold text-[#1d3557] font-mono">{inr(e.totals?.grand_total)}</div>
                <Link to={`/estimates/${e.id}`} className="text-[11.5px] font-semibold text-[#457b9d] hover:text-[#e63946] inline-flex items-center gap-1">
                  Open <ChevronRight size={11} />
                </Link>
              </div>
            </li>
          ))}
        </ul>
      ) : <Empty>No estimate linked yet.</Empty>}
      <div className="mt-3">
        <Link
          to={`/estimates/new?customer_id=${jc.customer_id || ""}&vehicle_id=${jc.vehicle_id || ""}&job_card_id=${jc.id}`}
          className="h-9 px-4 rounded-md border border-[#E4E7EC] text-[12px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE] inline-flex items-center gap-1"
          data-testid="jc-create-estimate-link"
        >
          <Plus size={11} /> Create estimate for this job
        </Link>
      </div>
    </Card>
  );
}

function PhotosTab({ jc, onReload }) {
  const files = jc.files || [];
  return (
    <Card title="Photos" testId="jc-photos">
      <PhotoUploader
        refId={jc.id}
        photos={files}
        context="job_card"
        onUploaded={() => onReload()}
        testId="jc-photos-uploader"
      />
    </Card>
  );
}

function InspectionTab({ jc, onReload }) {
  const [markers, setMarkers] = useState(jc.damage_markers || []);
  const [findings, setFindings] = useState(jc.findings || {});
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/job-cards/${jc.id}`, {
        customer_snapshot: jc.customer_snapshot,
        vehicle_snapshot: jc.vehicle_snapshot,
        complaints: jc.complaints || [],
        service_type: jc.service_type || "",
        repair_type: jc.repair_type || "direct_customer",
        service_advisor_id: jc.service_advisor_id || null,
        priority: jc.priority || "normal",
        damage_markers: markers,
      });
      await api.post(`/job-cards/${jc.id}/findings`, findings);
      toast.success("Inspection saved");
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save inspection");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="jc-inspection-tab">
      <Card title="Service-advisor findings" testId="jc-inspection-findings">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <FieldLabel>Initial observation</FieldLabel>
            <textarea rows={2} value={findings.initial_observation || ""}
              onChange={(e) => setFindings({ ...findings, initial_observation: e.target.value })}
              data-testid="jc-finding-initial"
              placeholder="Belt noise on cold start, minor scratches on rear bumper…"
              className="mt-1 w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
          </label>
          <label className="block">
            <FieldLabel>Probable cause</FieldLabel>
            <textarea rows={2} value={findings.probable_cause || ""}
              onChange={(e) => setFindings({ ...findings, probable_cause: e.target.value })}
              data-testid="jc-finding-cause"
              placeholder="Alternator belt wear, low coolant, brake pad…"
              className="mt-1 w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
          </label>
          <label className="block md:col-span-2">
            <FieldLabel>Recommended inspection</FieldLabel>
            <textarea rows={2} value={findings.recommended_inspection || ""}
              onChange={(e) => setFindings({ ...findings, recommended_inspection: e.target.value })}
              data-testid="jc-finding-recommend"
              className="mt-1 w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
          </label>
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-[12.5px] text-[#1d3557]">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={!!findings.immediate_attention}
              onChange={(e) => setFindings({ ...findings, immediate_attention: e.target.checked })}
              data-testid="jc-finding-immediate" className="w-3.5 h-3.5 accent-[#e63946]" />
            Immediate attention required
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={!!findings.road_test_required}
              onChange={(e) => setFindings({ ...findings, road_test_required: e.target.checked })}
              data-testid="jc-finding-roadtest" className="w-3.5 h-3.5 accent-[#e63946]" />
            Road test required
          </label>
        </div>
      </Card>

      <Card title="Vehicle condition (damage markers)" testId="jc-inspection-damage">
        <DamageMarker markers={markers} onChange={setMarkers} refId={jc.id} />
      </Card>

      <div className="flex justify-end">
        <button type="button" onClick={save} disabled={saving} data-testid="jc-inspection-save"
          className="h-10 px-5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
          <Check size={13} /> {saving ? "Saving…" : "Save inspection"}
        </button>
      </div>
    </div>
  );
}

const QC_ITEMS = [
  { key: "work_verified",          label: "All work items verified" },
  { key: "parts_fitted",           label: "Required parts fitted correctly" },
  { key: "fluids_checked",         label: "Fluid levels checked and topped up" },
  { key: "warning_lights_checked", label: "No warning lights on dashboard" },
  { key: "road_test_completed",    label: "Road test completed" },
  { key: "cleaned",                label: "Vehicle cleaned (optional)" },
  { key: "old_parts_returned",     label: "Old parts returned to customer" },
];

function QualityCheckTab({ jc, onReload }) {
  const [qc, setQc] = useState(() => ({
    work_verified: false, parts_fitted: false, fluids_checked: false,
    warning_lights_checked: false, road_test_completed: false,
    cleaned: false, old_parts_returned: false, notes: "",
    ...(jc.quality_check || {}),
  }));
  const [saving, setSaving] = useState(false);

  const requiredKeys = QC_ITEMS.filter((i) => i.key !== "cleaned").map((i) => i.key);
  const allRequired = requiredKeys.every((k) => qc[k]);
  const doneCount = QC_ITEMS.filter((i) => qc[i.key]).length;

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/job-cards/${jc.id}/quality-check`, qc);
      toast.success("Quality check saved");
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const signOff = async () => {
    if (!allRequired) {
      toast.error("Tick every required item before signing off.");
      return;
    }
    try {
      await api.post(`/job-cards/${jc.id}/quality-check`, qc);
      await api.post(`/job-cards/${jc.id}/status`, {
        status: "ready_for_delivery",
        note: "Quality check signed off",
      });
      toast.success("Signed off — vehicle Ready for Delivery");
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not sign off"); }
  };

  return (
    <div className="space-y-4" data-testid="jc-quality-tab">
      <Card title={`Quality check — ${doneCount}/${QC_ITEMS.length} completed`} testId="jc-quality-list">
        <ul className="space-y-2">
          {QC_ITEMS.map((it) => (
            <li key={it.key} className="flex items-start gap-3 border border-[#E4E7EC] rounded-lg p-3">
              <button
                type="button"
                onClick={() => setQc({ ...qc, [it.key]: !qc[it.key] })}
                data-testid={`jc-qc-${it.key}`}
                className={`shrink-0 w-6 h-6 rounded-full grid place-items-center transition-colors ${
                  qc[it.key]
                    ? "bg-[#12B76A] text-white"
                    : "border-2 border-[#E4E7EC] text-[#a8dadc]"
                }`}
                aria-label={`Toggle ${it.label}`}
              >
                {qc[it.key] ? <Check size={13} strokeWidth={3} /> : null}
              </button>
              <div className="flex-1">
                <div className={`text-[13px] ${qc[it.key] ? "text-[#0F9256] line-through" : "text-[#1d3557] font-medium"}`}>
                  {it.label}
                </div>
                {it.key === "cleaned" && <div className="text-[10.5px] text-[#667085]">Optional</div>}
              </div>
            </li>
          ))}
        </ul>
        <label className="block mt-3">
          <FieldLabel>Notes</FieldLabel>
          <textarea rows={2}
            value={qc.notes || ""}
            onChange={(e) => setQc({ ...qc, notes: e.target.value })}
            data-testid="jc-qc-notes"
            className="mt-1 w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
        </label>
        <div className="mt-3 flex flex-wrap gap-2 justify-end">
          <button type="button" onClick={save} disabled={saving}
            data-testid="jc-qc-save"
            className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE]">
            Save
          </button>
          <button type="button" onClick={signOff}
            disabled={!allRequired}
            data-testid="jc-qc-signoff"
            className="h-10 px-5 rounded-md bg-[#12B76A] hover:bg-[#0F9256] text-white text-[12.5px] font-semibold disabled:opacity-40 inline-flex items-center gap-1.5">
            <Check size={13} /> Sign off &amp; Ready for delivery
          </button>
        </div>
      </Card>

      {jc.quality_check?.at && (
        <div className="text-[11px] text-[#667085] italic px-2" data-testid="jc-qc-last-saved">
          Last saved {new Date(jc.quality_check.at).toLocaleString("en-IN")}.
        </div>
      )}
    </div>
  );
}

function PaymentsTab({ jc, onReload }) {
  const [f, setF] = useState({ received: false, amount: 0, mode: "cash", reference_number: "" });
  useEffect(() => { if (jc.advance) setF(jc.advance); }, [jc.advance]);
  const save = async () => {
    try { await api.post(`/job-cards/${jc.id}/advance`, f); toast.success("Advance saved"); onReload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  return (
    <Card title="Advance payment" testId="jc-payments">
      <label className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#1d3557]">
        <input type="checkbox" checked={f.received} onChange={(e) => setF({ ...f, received: e.target.checked })}
          data-testid="jc-pay-received" className="w-4 h-4 accent-[#e63946]" />
        Advance received
      </label>
      {f.received && (
        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
          <label className="block">
            <FieldLabel>Amount</FieldLabel>
            <input type="number" value={f.amount} data-testid="jc-pay-amount"
              onChange={(e) => setF({ ...f, amount: Number(e.target.value) })}
              className="mt-1 w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono" />
          </label>
          <label className="block">
            <FieldLabel>Mode</FieldLabel>
            <select value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })}
              data-testid="jc-pay-mode"
              className="mt-1 w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px]">
              {["cash","upi","card","bank_transfer","other"].map((m) => (
                <option key={m} value={m}>{m.replace("_", " ")}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <FieldLabel>Reference #</FieldLabel>
            <input type="text" value={f.reference_number} data-testid="jc-pay-ref"
              onChange={(e) => setF({ ...f, reference_number: e.target.value })}
              className="mt-1 w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px]" />
          </label>
        </div>
      )}
      <div className="mt-3">
        <button type="button" onClick={save} data-testid="jc-pay-save"
          className="h-10 px-4 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold">
          Save
        </button>
      </div>
    </Card>
  );
}

function FutureWorkTab({ jc, onReload }) {
  const [task, setTask] = useState("");
  const [reason, setReason] = useState("");
  const add = async () => {
    if (!task.trim()) return;
    await api.post(`/job-cards/${jc.id}/future-work`, { task, reason });
    setTask(""); setReason(""); toast.success("Future work added"); onReload();
  };
  const del = async (fid) => {
    await api.delete(`/job-cards/${jc.id}/future-work/${fid}`);
    onReload();
  };
  return (
    <Card title="Future suggested work" testId="jc-future">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
        <input value={task} onChange={(e) => setTask(e.target.value)}
          placeholder="Suggested task (e.g. Replace front brake pads within 2000 km)"
          data-testid="jc-future-task"
          className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px]" />
        <div className="flex gap-2">
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)"
            className="flex-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px]" />
          <button type="button" onClick={add} data-testid="jc-future-add"
            className="h-10 px-4 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold">
            <Plus size={13} className="inline mr-1" /> Add
          </button>
        </div>
      </div>
      {(jc.future_work || []).length === 0 ? <Empty>Nothing suggested yet.</Empty> : (
        <ul className="space-y-2">
          {jc.future_work.map((w) => (
            <li key={w.id} className="border border-[#E4E7EC] rounded-md p-3 flex items-start gap-3">
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-[#1d3557]">{w.task}</div>
                {w.reason && <div className="text-[11.5px] text-[#667085] mt-0.5">{w.reason}</div>}
              </div>
              <button type="button" onClick={() => del(w.id)} className="text-[#a8dadc] hover:text-[#e63946]">
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function DeliveryTab({ jc, onReload }) {
  const [invoices, setInvoices] = useState([]);
  const [gatePasses, setGatePasses] = useState([]);
  const [linksLoading, setLinksLoading] = useState(true);

  const loadLinks = useCallback(async () => {
    setLinksLoading(true);
    try {
      const [invRes, gpRes] = await Promise.all([
        api.get("/billing/invoices", { params: { job_card_id: jc.id } }),
        api.get("/billing/gate-passes", { params: { q: jc.number || jc.job_number || "" } }),
      ]);
      setInvoices((invRes.data || []).filter((i) => i.status !== "cancelled"));
      setGatePasses((gpRes.data || []).filter((g) => g.job_card_id === jc.id));
    } finally { setLinksLoading(false); }
  }, [jc.id, jc.number, jc.job_number]);
  useEffect(() => { loadLinks(); }, [loadLinks]);

  const activeInvoice = invoices.find((i) => i.status !== "cancelled");
  const activeGp = gatePasses.find((g) => g.status !== "cancelled");
  // Billable once the job has moved past mechanic work (QC onwards).
  const canBill = ["quality_check", "ready_for_delivery", "closed"].includes(jc.status);

  const deliver = async () => {
    if (!window.confirm("Deliver this vehicle now?")) return;
    await api.post(`/job-cards/${jc.id}/deliver`, {}).catch(() => {});
    toast.success("Vehicle delivered"); onReload();
  };
  const [na, setNa] = useState(jc.next_appointment || { required: false, suggested_date: "", service_type: "" });
  const saveNext = async () => {
    await api.post(`/job-cards/${jc.id}/next-appointment`, na);
    toast.success("Next appointment saved"); onReload();
  };
  return (
    <div className="space-y-4">
      {/* Billing & Gate Pass */}
      <Card title="Billing & vehicle exit" testId="jc-billing-block">
        {!canBill ? (
          <div className="rounded-md bg-[#FEF0C7] border border-[#F79009]/40 px-3 py-2.5 text-[12.5px] text-[#B54708]"
               data-testid="jc-billing-not-ready">
            Complete the work and mark the job card <b>Ready for delivery</b> to generate an invoice.
          </div>
        ) : linksLoading ? (
          <div className="text-[12.5px] text-[#667085]">Checking billing status…</div>
        ) : (
          <div className="space-y-3">
            {/* Invoice status */}
            {activeInvoice ? (
              <div className="rounded-lg border border-[#E4E7EC] px-3 py-2.5 flex items-center gap-3 flex-wrap"
                   data-testid="jc-existing-invoice">
                <div className="w-8 h-8 rounded-md bg-[#457b9d]/15 text-[#1d3557] grid place-items-center">
                  <FileText size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] uppercase tracking-wider font-semibold text-[#667085]">Invoice</div>
                  <div className="text-[13px] font-bold text-[#1d3557] font-mono">{activeInvoice.number}</div>
                  <div className="text-[11px] text-[#667085]">
                    {inr(activeInvoice.totals?.invoice_total)} · Balance {inr(activeInvoice.balance)}
                    · <span className="uppercase tracking-wider">{String(activeInvoice.status || "").replace(/_/g, " ")}</span>
                  </div>
                </div>
                <Link to={`/invoices/${activeInvoice.id}`} data-testid="jc-open-invoice"
                      className="h-9 px-3 rounded-md border border-[#E4E7EC] text-[12px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE] inline-flex items-center gap-1.5">
                  <ChevronRight size={12} /> Open invoice
                </Link>
              </div>
            ) : (
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0 text-[12.5px] text-[#1d3557]">
                  No invoice yet. Convert this completed job card into a customer invoice.
                </div>
                <Link to={`/invoices/new?job_card=${jc.id}`}
                      data-testid="jc-create-invoice-btn"
                      className="h-10 px-4 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5">
                  <FileText size={13} /> Create Invoice
                </Link>
              </div>
            )}

            {/* Gate Pass shortcut, once invoice exists */}
            {activeInvoice && !activeGp && (
              <div className="rounded-lg bg-[#F1FAEE] border border-[#a8dadc]/40 px-3 py-2.5 flex items-center gap-3 flex-wrap">
                <ShieldCheck size={14} className="text-[#1d3557]" />
                <div className="flex-1 text-[12px] text-[#457b9d]">
                  Issue a gate pass once the payment is settled to release the vehicle.
                </div>
                <Link to={`/gate-pass/new?invoice=${activeInvoice.id}`}
                      data-testid="jc-create-gate-pass-btn"
                      className="h-9 px-3 rounded-md bg-[#1d3557] hover:bg-[#152941] text-white text-[12px] font-semibold inline-flex items-center gap-1.5">
                  <ShieldCheck size={12} /> Create Gate Pass
                </Link>
              </div>
            )}
            {activeGp && (
              <div className="rounded-lg border border-[#12B76A]/40 bg-[#12B76A]/8 px-3 py-2.5 flex items-center gap-3 flex-wrap"
                   data-testid="jc-existing-gate-pass">
                <ShieldCheck size={14} className="text-[#0F9256]" />
                <div className="flex-1 text-[12.5px]">
                  <span className="uppercase tracking-wider text-[10.5px] font-semibold text-[#667085]">Gate Pass</span>
                  <div className="font-mono font-bold text-[#0F9256]">{activeGp.number}
                    <span className="ml-2 text-[10.5px] uppercase tracking-wider text-[#667085]">{String(activeGp.status || "").replace(/_/g, " ")}</span>
                  </div>
                </div>
                <Link to={`/gate-pass/${activeGp.id}`} data-testid="jc-open-gate-pass"
                      className="h-9 px-3 rounded-md border border-[#E4E7EC] text-[12px] font-semibold text-[#1d3557] hover:bg-white inline-flex items-center gap-1.5">
                  <ChevronRight size={12} /> Open
                </Link>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card title="Delivery" testId="jc-delivery">
        <div className="text-[13px] text-[#667085] mb-3">
          Deliver the vehicle to close the job card.
        </div>
        <button type="button" onClick={deliver}
          disabled={jc.status === "closed"}
          data-testid="jc-deliver-btn"
          className="h-10 px-4 rounded-md bg-[#12B76A] hover:bg-[#0F9256] text-white text-[12.5px] font-semibold disabled:opacity-40 inline-flex items-center gap-1.5">
          <Truck size={13} /> {jc.status === "closed" ? "Delivered & Closed" : "Deliver & Close"}
        </button>
      </Card>

      <Card title="Next appointment" testId="jc-next-appt">
        <label className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#1d3557]">
          <input type="checkbox" checked={!!na.required} onChange={(e) => setNa({ ...na, required: e.target.checked })}
            data-testid="jc-next-appt-req" className="w-4 h-4 accent-[#e63946]" />
          Suggest a next appointment
        </label>
        {na.required && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
            <input type="date" value={na.suggested_date || ""} onChange={(e) => setNa({ ...na, suggested_date: e.target.value })}
              data-testid="jc-next-appt-date"
              className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px]" />
            <input type="time" value={na.suggested_time || ""} onChange={(e) => setNa({ ...na, suggested_time: e.target.value })}
              data-testid="jc-next-appt-time"
              className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px]" />
            <select value={na.service_type || ""} onChange={(e) => setNa({ ...na, service_type: e.target.value })}
              data-testid="jc-next-appt-service"
              className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px]">
              <option value="">Service type…</option>
              {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
        <div className="mt-3 flex gap-2">
          <button type="button" onClick={saveNext} data-testid="jc-next-appt-save"
            className="h-9 px-4 rounded-md bg-[#1d3557] hover:bg-[#152941] text-white text-[12.5px] font-semibold">
            Save suggestion
          </button>
          {na.required && na.suggested_date && (
            <Link
              to={`/appointments`}
              className="h-9 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE] inline-flex items-center gap-1"
              data-testid="jc-next-appt-book"
            >
              <CalendarClock size={11} /> Book in calendar
            </Link>
          )}
        </div>
      </Card>
    </div>
  );
}

function ActivityTab({ jc }) {
  const items = [...(jc.activity || [])].reverse();
  return (
    <Card title="Activity" testId="jc-activity">
      {items.length === 0 ? <Empty>No activity yet.</Empty> : (
        <ol className="relative border-l border-[#E4E7EC] ml-2 space-y-3">
          {items.map((a) => (
            <li key={a.id} className="ml-3">
              <div className="absolute -left-1.5 w-3 h-3 rounded-full bg-[#457b9d]" />
              <div className="text-[13px] text-[#1d3557]">{a.note}</div>
              <div className="text-[10.5px] text-[#667085] mt-0.5 font-mono">
                {a.at ? new Date(a.at).toLocaleString("en-IN") : ""} · {a.type}
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

/* ---------- UI helpers ---------- */

function Card({ title, children, testId, action }) {
  return (
    <section className="bg-white rounded-xl border border-[#E4E7EC] p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[13.5px] font-bold text-[#1d3557]">{title}</div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Empty({ children }) {
  return <div className="text-[12.5px] text-[#667085] italic py-4">{children}</div>;
}

function FieldRow({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[10.5px] text-[#667085] uppercase tracking-wider font-semibold">{k}</span>
      <span className="text-[#1d3557] text-right truncate max-w-[60%]">{v || "—"}</span>
    </div>
  );
}

function FieldLabel({ children }) {
  return <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{children}</span>;
}

function StatusChipBig({ status }) {
  const meta = STATUS_META[status] || STATUS_META.draft;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${meta.chip}`}
      data-testid={`jc-status-big-${status}`}>
      {meta.label}
    </span>
  );
}

function StatusPicker({ current, open, onOpen, onChoose }) {
  return (
    <div className="relative">
      <button
        type="button" onClick={() => onOpen(!open)}
        data-testid="jc-status-picker"
        className="h-9 px-3 rounded-lg border-2 border-[#1d3557] bg-white text-[12.5px] font-bold text-[#1d3557] hover:bg-[#1d3557] hover:text-white transition-colors inline-flex items-center gap-2 shadow-sm"
      >
        <span className="inline-block w-2 h-2 rounded-full" style={{ background: STATUS_META[current]?.dot || "#667085" }} />
        Change Status <ChevronDown size={13} strokeWidth={2.5} />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 bg-white border border-[#E4E7EC] rounded-md shadow-lg py-1 min-w-[220px]" onMouseLeave={() => onOpen(false)}>
          {Object.entries(STATUS_META).map(([k, m]) => (
            <button
              key={k}
              onClick={() => { onChoose(k); onOpen(false); }}
              data-testid={`jc-status-choose-${k}`}
              className={`w-full text-left px-3 py-2 text-[12.5px] hover:bg-[#F1FAEE] ${k === current ? "font-bold text-[#1d3557] bg-[#F1FAEE]/50" : "text-[#1d3557]"}`}
            >
              <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: m.dot }} />
              {m.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
